import { Box, ButtonBase, Chip, Typography } from '@mui/material'
import { useId, useRef, type ReactNode } from 'react'

import { BORDER, FOCUS_BG, MUTED_TEXT, NAVY, NAVY_TINT, PANEL } from './styles'

interface FilterBoxProps {
  /** Lower-case noun for the labels: "locations", "keywords and tags". */
  noun: string
  open: boolean
  onOpenChange: (open: boolean) => void
  /** What is set underneath, shown while collapsed; null when nothing is set. */
  preview: ReactNode | null
  children: ReactNode
}

/**
 * A collapsible filter box. The header bar is the toggle, labelled Choose /
 * Edit / Hide; an open box also has a Hide bar at its foot.
 */
export default function FilterBox({
  noun,
  open,
  onOpenChange,
  preview,
  children,
}: FilterBoxProps) {
  const verb = open ? 'Hide' : preview ? 'Edit' : 'Choose'
  const headerRef = useRef<HTMLButtonElement>(null)
  const panelId = useId()
  const barSx = {
    width: '100%',
    justifyContent: 'space-between',
    px: 1.5,
    py: 1.1,
    fontSize: '14px',
    fontWeight: 500,
    fontFamily: 'inherit',
    // No ripple: the sweep on every open and close is motion nobody asked
    // for. Keyboard users get a plain outline instead.
    '&.Mui-focusVisible': { outline: `2px solid ${NAVY}`, outlineOffset: -3 },
  }

  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: open ? NAVY : BORDER,
        boxShadow: open ? `0 0 0 1px ${NAVY}` : 'none',
        borderRadius: 2,
        overflow: 'hidden',
        bgcolor: '#fff',
        my: 1,
      }}
    >
      <ButtonBase
        ref={headerRef}
        disableRipple
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        onClick={() => onOpenChange(!open)}
        sx={{
          ...barSx,
          bgcolor: open ? NAVY : PANEL,
          color: open ? '#fff' : NAVY,
          '&:hover': { bgcolor: open ? NAVY : FOCUS_BG },
          // The open bar is navy, so its focus ring is white.
          ...(open && { '&.Mui-focusVisible': { outline: '2px solid #fff', outlineOffset: -4 } }),
        }}
      >
        <span>{`${verb} ${noun}`}</span>
        <span aria-hidden="true">{open ? '▴' : '▾'}</span>
      </ButtonBase>

      {open ? (
        <>
          <Box
            id={panelId}
            role="region"
            aria-label={noun.charAt(0).toUpperCase() + noun.slice(1)}
            sx={{ px: 1.5, pt: 1.25, pb: 0.5 }}
          >
            {children}
          </Box>
          <ButtonBase
            disableRipple
            onClick={() => {
              onOpenChange(false)
              // This bar unmounts on close; focus the header so it isn't lost.
              headerRef.current?.focus()
            }}
            sx={{
              ...barSx,
              justifyContent: 'center',
              fontWeight: 400,
              color: NAVY,
              bgcolor: PANEL,
              borderTop: `1px solid ${BORDER}`,
              '&:hover': { bgcolor: FOCUS_BG },
            }}
          >
            {`Hide ${noun} `}
            <span aria-hidden="true">▴</span>
          </ButtonBase>
        </>
      ) : (
        preview && <Box sx={{ px: 1.5, pt: 0.25, pb: 1 }}>{preview}</Box>
      )}
    </Box>
  )
}

export function PreviewLine({
  label,
  chips = [],
}: {
  label: string
  chips?: string[]
}) {
  return (
    <Box sx={{ mt: 0.75 }}>
      <Typography sx={{ fontSize: '13px', color: MUTED_TEXT }}>{label}</Typography>
      {chips.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.25 }}>
          {chips.map((chip) => (
            <Chip
              key={chip}
              label={chip}
              size="small"
              sx={{ height: '20px', fontSize: '11px', fontWeight: 500, bgcolor: NAVY_TINT, color: NAVY }}
            />
          ))}
        </Box>
      )}
    </Box>
  )
}
